import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'

import { cx } from '@/libs/cva'

import {
  badgeColorVariants,
  badgeSizeVariants,
  type BadgeSizeVariantProps,
  type BadgeVariantProps,
} from './classname'

interface BadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'>,
    BadgeVariantProps,
    BadgeSizeVariantProps {
  asChild?: boolean
}

/**
 * This component is based on the `div` element and supports all of its props
 */
const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      className,
      /**
       * The badge's size.
       */
      size = 'base',
      /**
       * The style of the badge's border radius.
       */
      rounded = 'base',
      /**
       * The badge's color.
       */
      color = 'grey',
      /**
       * Whether to remove the wrapper `span` element and use the
       * passed child element instead.
       */
      asChild = false,
      ...props
    }: BadgeProps,
    ref
  ) => {
    const Component = asChild ? Slot : 'span'

    return (
      <Component
        ref={ref}
        className={cx(
          badgeColorVariants({ color }),
          badgeSizeVariants({ size, rounded }),
          className
        )}
        {...props}
      />
    )
  }
)
Badge.displayName = 'Badge'

export { Badge, badgeColorVariants }
